import { z } from 'zod';
import { currencySchema, productPurchaseSchema, refSchema } from '../../common';
import { objectIdSchema, priceSchema, quantitySchema } from '../../validation';

/**
 * Subscription snapshot — frozen at Billing finalize time.
 *
 * **One field, two jobs.** This snapshot is both:
 *
 * 1. The **pointer** to the live `Subscription` doc (`_id`, `ref`).
 * 2. The **pricing audit** (`purchase`, interval, qty fields, `pricing`,
 *    `addons`) — so historical reports survive later catalog edits.
 */
export const subscriptionSnapshotSchema = z.object({
  /** Subscription._id — internal Mongo ref. */
  _id: objectIdSchema,
  /** Mirrored from Subscription.ref (e.g. SUB-2026-000123). */
  ref: refSchema.optional(),
  /**
   * Frozen commercial identity at finalize — business, product, plan, unit.
   * Prefer this over joining the live Subscription.
   */
  purchase: productPurchaseSchema,
  /**
   * Billing interval at finalize (drives `monthsInInterval`).
   * Absent on a credit purchase.
   */
  interval: z.enum(['month', 'semester', 'year']).optional(),
  /** Currency at finalize (frozen on the subscription itself). */
  currency: currencySchema,
  committedVehicleQty: quantitySchema,
  actualFleetTotal: quantitySchema,
  overageVehicleQty: quantitySchema,
  /**
   * Order-volume audit for usage / legacy period-order Billings.
   * On a credit purchase, order qty fields carry the quantity bought.
   */
  committedOrdersForPeriod: quantitySchema.optional(),
  actualOrderTotal: quantitySchema.optional(),
  overageOrderQty: quantitySchema.optional(),
  pricing: z.object({
    basePricePerMonth: priceSchema,
    perVehiclePricePerMonth: priceSchema,
    overagePerVehiclePerMonth: priceSchema,
    /** Per-order rates (price of one order). Zero on a vehicle Billing. */
    perOrderPrice: priceSchema.optional(),
    overagePerOrderPrice: priceSchema.optional(),
    source: z.enum(['plan_default', 'org_override']),
  }),
  addons: z.array(
    z.object({
      code: z.string(),
      quantity: quantitySchema,
      /** Per-month unit price at the subscription's interval. */
      unitPricePerMonth: priceSchema,
      source: z.enum(['plan_default', 'org_override']),
    }),
  ),
});
export type SubscriptionSnapshot = z.infer<typeof subscriptionSnapshotSchema>;
